import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { PricingCard } from "./PricingCard";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  type CarouselApi,
} from "@/components/ui/carousel";

const PLANS = [
  { key: "allocation", isPopular: false },
  { key: "scope", isPopular: true },
  { key: "consulting", isPopular: false },
] as const;

export function PricingShowcase() {
  const { t } = useTranslation();
  const [api, setApi] = useState<CarouselApi>();
  const [activeIndex, setActiveIndex] = useState(0);

  const handleSelect = useCallback(() => {
    if (!api) return;
    setActiveIndex(api.selectedScrollSnap());
  }, [api]);

  useEffect(() => {
    if (!api) return;
    setActiveIndex(api.selectedScrollSnap());
    api.on("select", handleSelect);
    api.on("reInit", handleSelect);
    return () => {
      api.off("select", handleSelect);
    };
  }, [api, handleSelect]);

  const renderCard = (plan: (typeof PLANS)[number]) => (
    <PricingCard
      title={t(`pricing.${plan.key}.title`)}
      price={t(`pricing.${plan.key}.price`)}
      description={t(`pricing.${plan.key}.description`)}
      features={[0, 1, 2].map((i) => t(`pricing.${plan.key}.features.${i}`))}
      isPopular={plan.isPopular}
      buttonText={t("pricing.buttonText")}
      popularLabel={t("pricing.popular")}
    />
  );

  return (
    <>
      {/* Mobile: swipeable carousel, one plan at a time */}
      <div className="md:hidden pt-6">
        <Carousel opts={{ align: "center", loop: false }} setApi={setApi}>
          <CarouselContent className="-ml-4 pt-7">
            {PLANS.map((plan) => (
              <CarouselItem key={plan.key} className="basis-[86%] pl-4">
                {renderCard(plan)}
              </CarouselItem>
            ))}
          </CarouselContent>
        </Carousel>
        <div className="flex justify-center gap-2 mt-6">
          {PLANS.map((plan, index) => (
            <button
              key={plan.key}
              onClick={() => api?.scrollTo(index)}
              className={`h-2 rounded-full transition-all duration-300 ${
                activeIndex === index ? "w-6 bg-primary" : "w-2 bg-primary/30"
              }`}
              aria-label={`Ver plano ${index + 1}`}
            />
          ))}
        </div>
      </div>

      {/* Tablet/Desktop: full grid */}
      <div className="hidden md:grid md:grid-cols-3 gap-8 pt-6">
        {PLANS.map((plan) => (
          <div key={plan.key}>{renderCard(plan)}</div>
        ))}
      </div>
    </>
  );
}
